// accTables.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useApiData } from '../../contexts/apiService';
import { MarketPriceItem, StationCoordinateItem, ComponentPriceItem, ProducerPriceItem, OpisNglPriceItem } from '../../contexts/tableDefinitions'
import './accTables.css';
import Dropdown from '../../components/common/dropdown/dropdown';
import ToolBar from '../../components/common/ToolBar';
import { ErrorBoundary } from "react-error-boundary";
import DiscountBoxes from './marketComponent';
import DiscountDetailsPanel from './pricingPanel';
import LoaderWheel from '../../components/common/LoaderWheel';
import { useDataManager } from "../../contexts/dataManager";
import SQLTable from "../../components/sql_table/tableMain";
import { getColConfig, getInputConfig } from './accTablesConfig';
import TableInput from "../../components/common/table_input/tableInput";
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import { getUniqueDropdownOptions } from '../../helper_functions';

// Define the ApiData interface
interface ApiData {
    market_discounts: MarketPriceItem[];
    market_coordinates: StationCoordinateItem[];
    components_pricing: ComponentPriceItem[];
    producer_pricing: ProducerPriceItem[];
    opis_ngl_prices: OpisNglPriceItem[];
}

// Define column interface
interface TableColumn {
    id: string;
    header: string;
    accessorKey: string;
    filterFn: string;
    editable?: boolean;
    type?: 'text' | 'number' | 'dropdown';
    options?: Array<{ value: string; label: string }>;
    includeInAdd?: boolean,
    getDynamicOptions?: (apiData: ApiData, inputs: Record<string, string>) => Array<{ value: string; label: string }>;
    showLevel?: number;
}

// Define table configuration interface
interface TableConfig {
    title: string;
    columns: TableColumn[];
    sortBy: string[];
    showLevel?: number;
}

// OPIS component display order (matches COMPONENT_ORDER in ngl_mtd)
const OPIS_COMPONENT_ORDER: Record<string, number> = { C2: 0, C3: 1, IC4: 2, NC4: 3, 'C5+': 4 };
// Conway uses different labels for the same components
const OPIS_COMPONENT_ALIASES: Record<string, string> = { C2_EP: 'C2', C4: 'NC4' };
const OPIS_DEFAULT_GEOGRAPHY = 'Mont Belvieu Enterprise (Non-TET)';

const normalizeOpisComponent = (nickname: string | null | undefined): string => {
    const base = String(nickname ?? '').replace(/ MTD$/, '');
    return OPIS_COMPONENT_ALIASES[base] ?? base;
};

// Mont Belvieu Non-TET first, then remaining geographies alphabetically
const compareOpisGeography = (a: string, b: string): number => {
    if (a === b) return 0;
    if (a === OPIS_DEFAULT_GEOGRAPHY) return -1;
    if (b === OPIS_DEFAULT_GEOGRAPHY) return 1;
    return String(a ?? '').localeCompare(String(b ?? ''));
};

const AccTables = () => {
    const { apiData, isLoading, handleAddRow, handleDeleteRow, handleEditOrder, isEditLoading } = useApiData();
    const [activeTable, setActiveTable] = useState('producer_pricing');
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [selectedStationOwner, setSelectedStationOwner] = useState<string | null>(null);
    const [selectedStation, setSelectedStation] = useState<string | null>(null);
    const [activeRow, setActiveRow] = useState<any>(null);
    const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [opisView, setOpisView] = useState<'summary' | 'detail'>('summary');
    const { requestTableData, hasTableData } = useDataManager();
    const { userRole = 'viewer' } = useUserAccess();
    
    useEffect(() => {
        if (!hasTableData('producer_pricing')) {
            requestTableData('producer_pricing');
        }
        if (!hasTableData('components_pricing')) {
            requestTableData('components_pricing');
        }
        if (!hasTableData('market_discounts')) {
            requestTableData('market_discounts');
        }
        if (!hasTableData('market_coordinates')) {
            requestTableData('market_coordinates');
        }
        if (!hasTableData('lease_coordinates')) {
            requestTableData('lease_coordinates');
        }
        if (!hasTableData('producer_definition')) {
            requestTableData('producer_definition');
        }
        if (!hasTableData('entity_definition')) {
            requestTableData('entity_definition');
        }
        if (!hasTableData('carrier_definition')) {
            requestTableData('carrier_definition');
        }
        if (!hasTableData('opis_ngl_prices')) {
            requestTableData('opis_ngl_prices');
        }
    }, [activeTable]);

    // Get unique dropdown options for carrier_id
    const carrierOptions = getUniqueDropdownOptions({
        tableData: apiData?.carrier_definition,
        valueColumn: 'id',
        labelColumn: 'name',
    });

    const monthNameToNumber = (month: string) => {
        const map: Record<string, number> = {
            January: 1,
            February: 2,
            March: 3,
            April: 4,
            May: 5,
            June: 6,
            July: 7,
            August: 8,
            September: 9,
            October: 10,
            November: 11,
            December: 12,
        };
        return map[month] || 0;
    };

    // Get unique station owners from market_coordinates
    const stationOwnerOptions = useMemo(() => {
        const uniqueOwners = new Set(
            (apiData?.market_coordinates || [])
                .filter(item => !item.created_in_error)
                .map(item => item.market)
        );
        return Array.from(uniqueOwners)
            .filter(owner => owner) // Remove empty values
            .sort() // Sort alphabetically
            .map(owner => ({
                value: owner,
                label: owner
            }));
    }, [apiData?.market_coordinates]);

    const stationOptions = useMemo(() => {
        if (!apiData?.market_coordinates) return [];

        return apiData.market_coordinates
            .filter(item => !item.created_in_error && (!selectedStationOwner || item.market === selectedStationOwner))
            .map(item => ({ value: item.station, label: item.station }))
            .filter((item, index, self) => self.findIndex(t => t.value === item.value) === index) // Remove duplicates
            .sort((a, b) => a.label.localeCompare(b.label));

    }, [apiData?.market_coordinates, selectedStationOwner]);

    const offloadLaneOptions = useMemo(() => {
        if (!apiData?.market_coordinates || !selectedStationOwner || !selectedStation) return [];

        const filteredData = apiData.market_coordinates.filter(item =>
            !item.created_in_error &&
            item.market === selectedStationOwner &&
            item.station === selectedStation
        );

        // Group by unique market (offload lane)
        const uniqueMarkets = new Set(filteredData.map(item => item.market));

        return Array.from(uniqueMarkets)
            .filter(market => market)
            .map(market => ({
                value: market,
                label: market
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [apiData?.market_coordinates, selectedStationOwner, selectedStation]);

    const producerOptions = useMemo(() => {
        return (apiData?.producer_definition || [])
            .filter(item => item.is_active)
            .sort((a, b) => String(a.producer_name).localeCompare(String(b.producer_name)))
            .map(item => ({
                value: String(item.id),
                label: item.producer_name,
            }));
    }, [apiData?.producer_definition]);

    const getUniqueStations = (stationList: StationCoordinateItem[], station_owner: string) => {
        if (!stationList) return [];

        const filtered = stationList.filter(item => !item.created_in_error && item.market === station_owner);
        const uniqueLeases = new Set(filtered.map(item => item.station));

        return Array.from(uniqueLeases)
            .filter(lease => lease)
            .map(lease => ({ value: lease, label: lease }))
            .sort((a, b) => a.label.localeCompare(b.label));
    };

    // Define table configurations with dynamic options
    const TABLE_CONFIGS: Record<string, TableConfig> = useMemo(() => ({
        producer_pricing: {
            title: 'Producer Pricing',
            columns: [
                { id: 'id', header: 'ID', accessorKey: 'id', editable: false, filterFn: 'customFilter' },
                { id: 'producer', header: 'Producer', accessorKey: 'producer_id', editable: false, filterFn: 'customFilter', type: 'dropdown', options: producerOptions },
                { id: 'month', header: 'Month', accessorKey: 'month', editable: false, filterFn: 'customFilter'  },
                { id: 'year', header: 'Year', accessorKey: 'year', editable: false, filterFn: 'customFilter' },
            ],
            sortBy: ['producer'],
            showLevel: 3,
        },
        market_discounts: {
            title: 'Market Pricing',
            columns: [
                { id: 'id', header: 'ID', accessorKey: 'id', editable: false, filterFn: 'customFilter' },
                { id: 'station_owner', header: 'Station Owner', accessorKey: 'station_owner', editable: false, filterFn: 'customFilter', type: 'dropdown', options: stationOwnerOptions },
                { id: 'station', header: 'Station', accessorKey: 'station', editable: false, filterFn: 'customFilter', type: 'dropdown',
                    getDynamicOptions: (apiData: ApiData, inputs: Record<string, string>) => {
                        return getUniqueStations(apiData.market_coordinates, inputs.station_owner);
                    },
                },
                { id: 'market', header: 'Market', accessorKey: 'market', editable: false, filterFn: 'customFilter' },
                { id: 'month', header: 'Month', accessorKey: 'month', editable: false, filterFn: 'customFilter'  },
                { id: 'year', header: 'Year', accessorKey: 'year', editable: false, filterFn: 'customFilter' },
            ],
            sortBy: ['station_owner', 'station', 'market'],
            showLevel: 3,
        },
        carrier_pricing: {
            title: 'Carrier Pricing',
            columns: [
                { id: 'id', header: 'ID', accessorKey: 'id', editable: false, filterFn: 'customFilter' },
                { id: 'producer', header: 'Producer', accessorKey: 'producer', editable: false, filterFn: 'customFilter', type: 'dropdown', options: producerOptions },
                { id: 'month', header: 'Month', accessorKey: 'month', editable: false, filterFn: 'customFilter'  },
                { id: 'year', header: 'Year', accessorKey: 'year', editable: false, filterFn: 'customFilter' },
            ],
            sortBy: ['producer'],
            showLevel: 3,
        },
        components_pricing: {
            title: 'Components Pricing',
            columns: [],
            sortBy: [],
            showLevel: 3,
        },
        opis_ngl_prices: {
            title: 'OPIS Components',
            columns: [],
            sortBy: [],
            showLevel: 3,
        },
    }), [stationOwnerOptions, stationOptions, producerOptions, offloadLaneOptions]);

    const ACCOUNTANT = USER_ROLE_LEVEL['accountant'];
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= ACCOUNTANT;

    const sortedData = useMemo(() => {
        let data: any[] = [];
        if (activeTable === 'market_discounts') {
            data = apiData?.market_discounts ?? [];
            data = Array.isArray(data) ? data.filter(item => item.main === 1) : [];

        } else if (activeTable === 'components_pricing') {
            data = apiData?.components_pricing ?? [];

        } else if (activeTable === 'carrier_pricing') {
            data = apiData?.carrier_pricing ?? [];

        } else if (activeTable === 'opis_ngl_prices') {
            if (opisView === 'summary') {
                // One row per date × geography × component with Spot and MTD side by side (mirrors ngl_mtd pricing reference)
                const rawData = apiData?.opis_ngl_prices ?? [];
                const byKey = new Map<string, Record<string, any>>();
                let syntheticId = 0;
                for (const row of rawData) {
                    const date = String(row.price_date);
                    const component = normalizeOpisComponent(row.nickname);
                    const key = `${date}|${row.geography}|${component}`;
                    if (!byKey.has(key)) {
                        byKey.set(key, { id: `opis_summary_${syntheticId++}`, price_date: date, geography: row.geography, component, spot_value: null, mtd_value: null });
                    }
                    const isMtd = row.value_type
                        ? String(row.value_type).toLowerCase() === 'mtd'
                        : String(row.nickname ?? '').endsWith(' MTD');
                    byKey.get(key)![isMtd ? 'mtd_value' : 'spot_value'] = row.value;
                }
                data = Array.from(byKey.values());
            } else {
                data = apiData?.opis_ngl_prices ?? [];
            }

        } else if (activeTable === 'producer_pricing') {
            data = apiData?.producer_pricing ?? [];
            data = Array.isArray(data) ? data.filter(item => item.main === 1) : [];
        }
        if (!Array.isArray(data)) return [];

        if (activeTable === 'producer_pricing' || activeTable === 'market_discounts') {
            const uniqueMap = new Map<string, any>();
            data.forEach(item => {
                let key = '';
                if (activeTable === 'producer_pricing') {
                    key = item.producer;

                } else if (activeTable === 'market_discounts') {
                    key = `${item.station_owner}_${item.market}_${item.station}`;
                }
                const year = Number(item.year) || 0;
                const monthNum = monthNameToNumber(item.month);
                if (!uniqueMap.has(key)) {
                    uniqueMap.set(key, item);
                } else {
                    const existing = uniqueMap.get(key);
                    const existingYear = Number(existing.year) || 0;
                    const existingMonth = monthNameToNumber(existing.month);
                    // Update if the current item is newer (higher year or same year but later month)
                    if (year > existingYear || (year === existingYear && monthNum > existingMonth)) {
                        uniqueMap.set(key, item);
                    }
                }
            });
            data = Array.from(uniqueMap.values());
        }

        return data.sort((a, b) => {
            if (activeTable === 'components_pricing') {
                // sorting component pricing newest on top
                const yearA = Number(a.year) || 0;
                const yearB = Number(b.year) || 0;

                if (yearA !== yearB) {
                    return yearB - yearA;
                }

                const monthNumA = monthNameToNumber(a.month);
                const monthNumB = monthNameToNumber(b.month);

                return monthNumB - monthNumA;
            }

            else if (activeTable === 'opis_ngl_prices') {
                const tsA = new Date(a.price_date).getTime();
                const tsB = new Date(b.price_date).getTime();
                if (tsA !== tsB) return tsB - tsA;
                const geoCmp = compareOpisGeography(a.geography, b.geography);
                if (geoCmp !== 0) return geoCmp;
                const compA = opisView === 'summary' ? a.component : normalizeOpisComponent(a.nickname);
                const compB = opisView === 'summary' ? b.component : normalizeOpisComponent(b.nickname);
                const orderCmp = (OPIS_COMPONENT_ORDER[compA] ?? 99) - (OPIS_COMPONENT_ORDER[compB] ?? 99);
                if (orderCmp !== 0 || opisView === 'summary') return orderCmp;
                // Detail: spot before MTD for the same component
                return String(a.value_type ?? '').localeCompare(String(b.value_type ?? ''));
            }

            else {
                for (const key of TABLE_CONFIGS[activeTable].sortBy) {
                    const valueA = a[key] ?? '';
                    const valueB = b[key] ?? '';
                    if (valueA !== valueB) {
                        if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
                            return Number(valueA) - Number(valueB);
                        }
                        return String(valueA).localeCompare(String(valueB));
                    }
                }
                return 0;
            }

        });
    }, [apiData, activeTable, TABLE_CONFIGS, currentMonth, currentYear, opisView]);


    const handleAdd = (newItem: Record<string, string>) => {
        if (activeTable === 'market_discounts') {
            newItem.main = '1';
            newItem.discount = '0';
            newItem.is_active = '1';
            newItem.station_charge = '0';
        }

        if (activeTable === 'producer_pricing') {
            newItem.main = '1';
            newItem.discount = '0';
            newItem.is_active = '1';
            newItem.is_split_deal = '0';
            newItem.is_pickedup_deal = '0';
            newItem.rockbridge_title = '1';
            const producerDef = (apiData?.producer_definition || []).find(
                p => String(p.id) === String(newItem.producer_id)
            );
            if (producerDef) newItem.producer = producerDef.producer_name;
        }
        handleAddRow(activeTable, newItem);
    };

    const handleDeleteSelectedRows = () => {
        if (selectedRows.size > 0) {
            handleDeleteRow(selectedRows, activeTable);
            setSelectedRows(new Set());
        }
    };

    // Function to convert table columns to toolbar format
    const convertToToolbarColumns = (columns: TableColumn[]) => {
        return columns
            .filter(
                col => col.includeInAdd !== false &&
                    col.accessorKey !== 'id'
            )
            .map(col => ({
                header: col.header,
                accessor: col.accessorKey,
                type: col.type,
                options: col.options,
                getDynamicOptions: col.getDynamicOptions,
            }));
    };

    if (isLoading) return <LoaderWheel />;

    const tableOptions = Object.entries(TABLE_CONFIGS).map(([value, config]) => ({
        value,
        label: config.title
    }));
    
    if (!showTable) {
        return <p>Not Allowed</p>;
    }

    return (
        <div className="rt-mainContainer">
            {activeRow && activeTable === 'market_discounts' && (
                <DiscountDetailsPanel
                    discount={activeRow}
                    allDiscounts={apiData?.market_discounts ?? []}
                    onClose={() => setActiveRow(null)}
                    handleEditOrder={handleEditOrder}
                    handleDeleteRow={handleDeleteRow}
                    handleAddRow={handleAddRow}
                    activeTable={'market_discounts'}
                />
            )}
            {activeRow && activeTable === 'producer_pricing' && (
                <DiscountDetailsPanel
                    discount={activeRow}
                    allDiscounts={apiData?.producer_pricing ?? []}
                    onClose={() => setActiveRow(null)}
                    handleEditOrder={handleEditOrder}
                    handleDeleteRow={handleDeleteRow}
                    handleAddRow={handleAddRow}
                    activeTable={'producer_pricing'}
                />
            )}
            <div className="rt-topContainer">
                <div className="rt-dropdownContainer">
                    <Dropdown
                        options={tableOptions}
                        value={activeTable}
                        onChange={(value) => {
                            setActiveTable(value);
                            setSelectedRows(new Set());
                            setActiveRow(null);
                        }}
                        className="dropdown-inputMatch"
                    />
                </div>
                <div className="rt-toolbarContainer">

                    {(activeTable === 'components_pricing' || activeTable === 'carrier_pricing') ? (
                        <div className="scheduleTable-inputContainer">
                            <TableInput
                                tableName={activeTable}
                                inputColumnConfigs={getInputConfig(carrierOptions)[activeTable].inputColumnConfigs}
                                addMultipleRows={getInputConfig(carrierOptions)[activeTable].addMultipleRows}
                            />
                        </div>)

                        : activeTable !== 'opis_ngl_prices' ? (
                            <ToolBar
                                columns={convertToToolbarColumns(TABLE_CONFIGS[activeTable].columns)}
                                onAdd={handleAdd}
                                handleDeleteRow={handleDeleteSelectedRows}
                                selectedTicketsCount={selectedRows.size}
                                activeTable={activeTable}
                            />
                        ) : activeTable === 'opis_ngl_prices' ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                    onClick={() => setOpisView('summary')}
                                    style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        cursor: 'pointer',
                                        fontWeight: opisView === 'summary' ? 700 : 400,
                                        background: opisView === 'summary' ? '#1a73e8' : '#fff',
                                        color: opisView === 'summary' ? '#fff' : '#333',
                                    }}
                                >Summary</button>
                                <button
                                    onClick={() => setOpisView('detail')}
                                    style={{
                                        padding: '4px 12px',
                                        borderRadius: '4px',
                                        border: '1px solid #ccc',
                                        cursor: 'pointer',
                                        fontWeight: opisView === 'detail' ? 700 : 400,
                                        background: opisView === 'detail' ? '#1a73e8' : '#fff',
                                        color: opisView === 'detail' ? '#fff' : '#333',
                                    }}
                                >Detail</button>
                            </div>
                        ) : null
                    }
                </div>
            </div>
            <ErrorBoundary fallback={<div>Error loading table</div>}>
                <div>
                    {(activeTable === 'components_pricing' || activeTable === 'carrier_pricing' || activeTable === 'opis_ngl_prices') && (
                        <div className="rt-tableContainer">
                            <SQLTable
                                tableName={activeTable}
                                tableData={sortedData}
                                persistFilters={true}
                                idColumn={'id'}
                                columnConfigs={
                                    activeTable === 'opis_ngl_prices' && opisView === 'summary'
                                        ? getColConfig()['opis_ngl_summary'].columns
                                        : getColConfig()[activeTable].columns
                                }
                                selectableRows={activeTable !== 'opis_ngl_prices'}
                                deletableRows={activeTable !== 'opis_ngl_prices'}
                                userRoleLevel={USER_ROLE_LEVEL[userRole] || 1}
                                showTable={showTable}
                                sortOrder={
                                    activeTable === 'opis_ngl_prices' && opisView === 'summary'
                                        ? getColConfig()['opis_ngl_summary'].sortOrder
                                        : getColConfig()[activeTable].sortOrder
                                }
                            />
                        </div>
                    )}
                    {activeTable === 'market_discounts' && (
                        <div className="rt-tableContainer2">
                            <DiscountBoxes
                                discounts={sortedData}
                                onBoxClick={(discount) => setActiveRow(discount)}
                                activeTable={'market_discounts'}
                            />
                        </div>
                    )}
                    {activeTable === 'producer_pricing' && (
                        <div className="rt-tableContainer2">
                            <DiscountBoxes
                                discounts={sortedData}
                                onBoxClick={(discount) => setActiveRow(discount)}
                                activeTable={'producer_pricing'}
                            />
                        </div>
                    )}
                </div>
            </ErrorBoundary>
        </div>
    );
};

export default AccTables;